// AdminConfig.js - Admin configuration panel for SMS templates and tag rules
import React, { useState, useEffect } from 'react';
import { getBaseURL } from '../config/api';
import axios from 'axios';
import AlarmSettings from './AlarmSettings';


const api = axios.create({
  baseURL: `${getBaseURL()}/api`,
  withCredentials: true,
});

function AdminConfig() {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [tagRules, setTagRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ platform: '', order_type: '', message_template: '' });
  const [newRule, setNewRule] = useState({ tag_name: '', keywords: '' });
  const [showNewTemplateForm, setShowNewTemplateForm] = useState(false);
  const [showNewRuleForm, setShowNewRuleForm] = useState(false);
  const [previewName, setPreviewName] = useState('John');
  const [reprocessStartDate, setReprocessStartDate] = useState('');
  const [reprocessEndDate, setReprocessEndDate] = useState('');
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);
  const [toastBackfilling, setToastBackfilling] = useState(false);
  const [toastBackfillResult, setToastBackfillResult] = useState(null);
  const [toastModifiersProgress, setToastModifiersProgress] = useState(null);
  const [emailItemsBackfilling, setEmailItemsBackfilling] = useState(false);
  const [emailItemsResult, setEmailItemsResult] = useState(null);
  const [emailItemsOutput, setEmailItemsOutput] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templatesRes, rulesRes] = await Promise.all([
        api.get('/admin/sms-templates'),
        api.get('/admin/tag-rules')
      ]);
      setTemplates(templatesRes.data);
      setTagRules(rulesRes.data);
    } catch (err) {
      setError('Failed to load configuration data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Template functions
  const handleSaveTemplate = async (template) => {
    try {
      if (template.id) {
        await api.put(`/admin/sms-templates/${template.id}`, template);
      } else {
        await api.post('/admin/sms-templates', template);
      }
      setEditingTemplate(null);
      setShowNewTemplateForm(false);
      setNewTemplate({ platform: '', order_type: '', message_template: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/admin/sms-templates/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete template');
    }
  };

  // Tag rule functions
  const handleSaveRule = async (rule) => {
    try {
      const ruleData = {
        ...rule,
        keywords: typeof rule.keywords === 'string' ? rule.keywords : rule.keywords.join(', ')
      };
      if (rule.id) {
        await api.put(`/admin/tag-rules/${rule.id}`, ruleData);
      } else {
        await api.post('/admin/tag-rules', ruleData);
      }
      setEditingRule(null);
      setShowNewRuleForm(false);
      setNewRule({ tag_name: '', keywords: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save tag rule');
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tag rule?')) return;
    try {
      await api.delete(`/admin/tag-rules/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete tag rule');
    }
  };

  const previewMessage = (template) => {
    return template.replace('{{first_name}}', previewName);
  };

  if (loading) {
    return <div className="p-4 text-center">Loading configuration...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <h1 className="text-2xl font-bold mb-6">Email Scraper Configuration</h1>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-700 mb-6">
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'templates' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
          onClick={() => setActiveTab('templates')}
        >
          Message Templates
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'rules' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
          onClick={() => setActiveTab('rules')}
        >
          Tag Rules
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'reprocess' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
          onClick={() => setActiveTab('reprocess')}
        >
          Reprocess Tags
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'alarms' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
          onClick={() => setActiveTab('alarms')}
        >
          🚨 Order Alarms
        </button>
      </div>

      {activeTab === 'alarms' && <AlarmSettings />}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">SMS Templates</h2>
            <button
              onClick={() => setShowNewTemplateForm(!showNewTemplateForm)}
              className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded"
            >
              + Add Template
            </button>
          </div>

          {/* Preview Name Input */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-gray-400">Preview name:</span>
            <input
              type="text"
              value={previewName}
              onChange={(e) => setPreviewName(e.target.value)}
              className="bg-gray-700 text-white px-2 py-1 rounded w-32"
            />
          </div>

          {/* New Template Form */}
          {showNewTemplateForm && (
            <div className="bg-gray-800 p-4 rounded mb-4">
              <h3 className="font-semibold mb-3">New Template</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <input
                  type="text"
                  placeholder="Platform (e.g., GrubHub)"
                  value={newTemplate.platform}
                  onChange={(e) => setNewTemplate({ ...newTemplate, platform: e.target.value })}
                  className="bg-gray-700 text-white px-3 py-2 rounded"
                />
                <input
                  type="text"
                  placeholder="Order Type (e.g., Pickup)"
                  value={newTemplate.order_type}
                  onChange={(e) => setNewTemplate({ ...newTemplate, order_type: e.target.value })}
                  className="bg-gray-700 text-white px-3 py-2 rounded"
                />
              </div>
              <textarea
                placeholder="Message template (use {{first_name}} for personalization)"
                value={newTemplate.message_template}
                onChange={(e) => setNewTemplate({ ...newTemplate, message_template: e.target.value })}
                className="bg-gray-700 text-white px-3 py-2 rounded w-full h-24 mb-3"
              />
              {newTemplate.message_template && (
                <div className="mb-3 p-2 bg-gray-900 rounded">
                  <span className="text-gray-400 text-sm">Preview: </span>
                  <span className="text-green-400">{previewMessage(newTemplate.message_template)}</span>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => handleSaveTemplate(newTemplate)}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => { setShowNewTemplateForm(false); setNewTemplate({ platform: '', order_type: '', message_template: '' }); }}
                  className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Templates Table */}
          <div className="overflow-x-auto">
            <table className="w-full bg-gray-800 rounded">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-4 py-3 text-left">Platform</th>
                  <th className="px-4 py-3 text-left">Order Type</th>
                  <th className="px-4 py-3 text-left">Message Template</th>
                  <th className="px-4 py-3 text-left">Preview</th>
                  <th className="px-4 py-3 text-left">Active</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id} className="border-b border-gray-700 hover:bg-gray-750">
                    {editingTemplate?.id === template.id ? (
                      <>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editingTemplate.platform}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, platform: e.target.value })}
                            className="bg-gray-700 text-white px-2 py-1 rounded w-full"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editingTemplate.order_type}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, order_type: e.target.value })}
                            className="bg-gray-700 text-white px-2 py-1 rounded w-full"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <textarea
                            value={editingTemplate.message_template}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, message_template: e.target.value })}
                            className="bg-gray-700 text-white px-2 py-1 rounded w-full h-20"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-green-400">
                          {previewMessage(editingTemplate.message_template)}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={editingTemplate.is_active}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, is_active: e.target.checked })}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleSaveTemplate(editingTemplate)}
                            className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded mr-2 text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingTemplate(null)}
                            className="bg-gray-600 hover:bg-gray-700 px-2 py-1 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">{template.platform}</td>
                        <td className="px-4 py-3">{template.order_type}</td>
                        <td className="px-4 py-3 text-sm max-w-xs truncate">{template.message_template}</td>
                        <td className="px-4 py-3 text-sm text-green-400 max-w-xs truncate">
                          {previewMessage(template.message_template)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={template.is_active ? 'text-green-400' : 'text-red-400'}>
                            {template.is_active ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setEditingTemplate({ ...template })}
                            className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded mr-2 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="bg-red-600 hover:bg-red-700 px-2 py-1 rounded text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tag Rules Tab */}
      {activeTab === 'rules' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Tag Rules</h2>
            <button
              onClick={() => setShowNewRuleForm(!showNewRuleForm)}
              className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded"
            >
              + Add Rule
            </button>
          </div>

          {/* New Rule Form */}
          {showNewRuleForm && (
            <div className="bg-gray-800 p-4 rounded mb-4">
              <h3 className="font-semibold mb-3">New Tag Rule</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <input
                  type="text"
                  placeholder="Tag Name (e.g., Vegan)"
                  value={newRule.tag_name}
                  onChange={(e) => setNewRule({ ...newRule, tag_name: e.target.value })}
                  className="bg-gray-700 text-white px-3 py-2 rounded"
                />
                <input
                  type="text"
                  placeholder="Keywords (comma-separated, e.g., tofu, impossible)"
                  value={newRule.keywords}
                  onChange={(e) => setNewRule({ ...newRule, keywords: e.target.value })}
                  className="bg-gray-700 text-white px-3 py-2 rounded"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSaveRule(newRule)}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => { setShowNewRuleForm(false); setNewRule({ tag_name: '', keywords: '' }); }}
                  className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Tag Rules Table */}
          <div className="overflow-x-auto">
            <table className="w-full bg-gray-800 rounded">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-4 py-3 text-left">Tag Name</th>
                  <th className="px-4 py-3 text-left">Keywords</th>
                  <th className="px-4 py-3 text-left">Active</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tagRules.map((rule) => (
                  <tr key={rule.id} className="border-b border-gray-700 hover:bg-gray-750">
                    {editingRule?.id === rule.id ? (
                      <>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editingRule.tag_name}
                            onChange={(e) => setEditingRule({ ...editingRule, tag_name: e.target.value })}
                            className="bg-gray-700 text-white px-2 py-1 rounded w-full"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={Array.isArray(editingRule.keywords) ? editingRule.keywords.join(', ') : editingRule.keywords}
                            onChange={(e) => setEditingRule({ ...editingRule, keywords: e.target.value })}
                            className="bg-gray-700 text-white px-2 py-1 rounded w-full"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={editingRule.is_active}
                            onChange={(e) => setEditingRule({ ...editingRule, is_active: e.target.checked })}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleSaveRule(editingRule)}
                            className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded mr-2 text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingRule(null)}
                            className="bg-gray-600 hover:bg-gray-700 px-2 py-1 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-medium">{rule.tag_name}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(Array.isArray(rule.keywords) ? rule.keywords : []).map((kw, idx) => (
                              <span key={idx} className="bg-gray-700 px-2 py-1 rounded text-sm">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={rule.is_active ? 'text-green-400' : 'text-red-400'}>
                            {rule.is_active ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setEditingRule({ ...rule })}
                            className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded mr-2 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="bg-red-600 hover:bg-red-700 px-2 py-1 rounded text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reprocess Tags Tab */}
      {activeTab === 'reprocess' && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Reprocess Tags</h2>
          <p className="text-gray-400 mb-4">
            Re-apply current tag rules to all orders (Toast and Email) within a date range.
            Use this after adding or updating tag rules to backfill tags on historical orders.
          </p>

          <div className="flex gap-4 items-end mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={reprocessStartDate}
                onChange={e => setReprocessStartDate(e.target.value)}
                className="bg-gray-700 text-white px-3 py-2 rounded border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={reprocessEndDate}
                onChange={e => setReprocessEndDate(e.target.value)}
                className="bg-gray-700 text-white px-3 py-2 rounded border border-gray-600"
              />
            </div>
            <button
              onClick={async () => {
                if (!reprocessStartDate || !reprocessEndDate) {
                  alert('Please select both start and end dates');
                  return;
                }
                setReprocessing(true);
                setReprocessResult(null);
                try {
                  const res = await api.post('/admin/reprocess-tags', {
                    startDate: reprocessStartDate,
                    endDate: reprocessEndDate
                  });
                  setReprocessResult(res.data);
                } catch (err) {
                  setReprocessResult({ error: err.response?.data?.error || 'Failed to reprocess tags' });
                } finally {
                  setReprocessing(false);
                }
              }}
              disabled={reprocessing}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded font-medium"
            >
              {reprocessing ? 'Reprocessing...' : 'Reprocess Tags'}
            </button>
          </div>

          {reprocessResult && (
            <div className={`p-4 rounded mb-6 ${reprocessResult.error ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'}`}>
              {reprocessResult.error
                ? `Error: ${reprocessResult.error}`
                : `Updated ${reprocessResult.updated} orders`}
            </div>
          )}

          <hr className="border-gray-700 my-6" />

          <h2 className="text-xl font-semibold mb-4">Backfill Toast Modifiers</h2>
          <p className="text-gray-400 mb-4">
            Re-fetch Toast orders from the Toast API and update item data to include modifiers
            (add-ons like Shrimp, Extra Cheese, etc. that were previously not stored).
            Uses the same date range selected above.
          </p>

          <button
            onClick={async () => {
              if (!reprocessStartDate || !reprocessEndDate) {
                alert('Please select both start and end dates above');
                return;
              }
              setToastBackfilling(true);
              setToastBackfillResult(null);
              setToastModifiersProgress(null);
              try {
                const res = await api.post('/admin/backfill-toast-modifiers', {
                  startDate: reprocessStartDate,
                  endDate: reprocessEndDate
                });
                setToastBackfillResult(res.data);
                // Poll for progress
                const pollInterval = setInterval(async () => {
                  try {
                    const outputRes = await api.get('/admin/backfill-toast-modifiers/output');
                    if (outputRes.data.progress) {
                      setToastModifiersProgress(outputRes.data.progress);
                    }
                    if (!outputRes.data.running) {
                      clearInterval(pollInterval);
                      setToastBackfilling(false);
                      // Show final result
                      if (outputRes.data.progress) {
                        const p = outputRes.data.progress;
                        setToastBackfillResult({
                          message: `Updated ${p.updated} of ${p.total} Toast orders with modifiers (${p.errors} errors)`
                        });
                      }
                    }
                  } catch (e) {
                    clearInterval(pollInterval);
                    setToastBackfilling(false);
                  }
                }, 2000);
              } catch (err) {
                setToastBackfillResult({ error: err.response?.data?.error || 'Failed to start Toast modifiers backfill' });
                setToastBackfilling(false);
              }
            }}
            disabled={toastBackfilling}
            className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 px-4 py-2 rounded font-medium"
          >
            {toastBackfilling ? 'Backfilling...' : 'Backfill Toast Modifiers'}
          </button>

          {toastModifiersProgress && toastBackfilling && (
            <div className="p-4 rounded mt-4 bg-blue-900/50 text-blue-300">
              Processing {toastModifiersProgress.processed}/{toastModifiersProgress.total} orders...
              {toastModifiersProgress.updated > 0 && ` (${toastModifiersProgress.updated} updated)`}
              {toastModifiersProgress.errors > 0 && ` (${toastModifiersProgress.errors} errors)`}
            </div>
          )}

          {toastBackfillResult && !toastBackfilling && (
            <div className={`p-4 rounded mt-4 ${toastBackfillResult.error ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'}`}>
              {toastBackfillResult.error
                ? `Error: ${toastBackfillResult.error}`
                : toastBackfillResult.message}
            </div>
          )}

          <hr className="border-gray-700 my-6" />

          <h2 className="text-xl font-semibold mb-4">Backfill Email Items & Modifiers</h2>
          <p className="text-gray-400 mb-4">
            Re-fetch ChowNow and GrubHub order emails from Gmail and extract item details
            including modifiers (add-ons like Shrimp, Guacamole, etc.) for orders that are
            missing items data. Uses the same date range selected above.
          </p>

          <button
            onClick={async () => {
              if (!reprocessStartDate || !reprocessEndDate) {
                alert('Please select both start and end dates above');
                return;
              }
              setEmailItemsBackfilling(true);
              setEmailItemsResult(null);
              setEmailItemsOutput([]);
              try {
                const res = await api.post('/admin/backfill-email-items', {
                  startDate: reprocessStartDate,
                  endDate: reprocessEndDate
                });
                setEmailItemsResult(res.data);
                // Poll for output
                const pollInterval = setInterval(async () => {
                  try {
                    const outputRes = await api.get('/admin/backfill-email-items/output');
                    if (outputRes.data.output) {
                      setEmailItemsOutput(outputRes.data.output);
                    }
                    if (!outputRes.data.running) {
                      clearInterval(pollInterval);
                      setEmailItemsBackfilling(false);
                    }
                  } catch (e) {
                    clearInterval(pollInterval);
                    setEmailItemsBackfilling(false);
                  }
                }, 3000);
              } catch (err) {
                setEmailItemsResult({ error: err.response?.data?.error || 'Failed to start email items backfill' });
                setEmailItemsBackfilling(false);
              }
            }}
            disabled={emailItemsBackfilling}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-4 py-2 rounded font-medium"
          >
            {emailItemsBackfilling ? 'Backfilling...' : 'Backfill Email Items'}
          </button>

          {emailItemsResult && (
            <div className={`p-4 rounded mt-4 ${emailItemsResult.error ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'}`}>
              {emailItemsResult.error
                ? `Error: ${emailItemsResult.error}`
                : emailItemsResult.message}
            </div>
          )}

          {emailItemsOutput.length > 0 && (
            <div className="mt-4 bg-gray-900 p-4 rounded border border-gray-700 max-h-64 overflow-y-auto">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Output:</h3>
              {emailItemsOutput.map((line, i) => (
                <div key={i} className="text-xs text-gray-300 font-mono">{line}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminConfig;
